import { api } from './api.ts'
import type { Cartella, PaginaThread, StatoThread, VoceThread } from './posta.ts'

/**
 * Lettura continua della posta (31/10/2026): le chiamate dei due temporizzatori della pagina Posta.
 *
 *  - ogni minuto `chiediLettura`: l'API accoda la lettura delle caselle guardate, se l'ultima ha
 *    piu' di un minuto e non ce n'e' gia' una in coda, e dice a che punto e' ciascuna;
 *  - ogni trenta secondi `rileggiElenco` e `rileggiCartelle`: la prima pagina delle conversazioni e i
 *    conteggi dei non letti, dal database e senza toccare il server di posta.
 *
 * Nessuna delle due rinnova la sessione: la prima e' una rotta di polling, le altre si dichiarano
 * automatiche (`automatica: true`). E' l'inattivita' della persona che deve chiudere la sessione,
 * non quella dei temporizzatori.
 */

export type StatoLettura = {
  id: string
  leggibile: boolean
  ultima_lettura: string | null
  errore: boolean
  in_coda: boolean
  in_lettura: boolean
  accodata: boolean
}

export type EsitoLettura = {
  modo: 'integrale' | 'selettiva' | null
  /** Il Job parte subito: senza, i lavori aspettano il giro orario. */
  avvio_immediato: boolean
  caselle: StatoLettura[]
}

/** Senza caselle valgono quelle dell'utente: la propria e quelle di gruppo. */
export const chiediLettura = (caselle?: string[]) =>
  api.post<EsitoLettura>('/posta/lettura-continua', caselle?.length ? { caselle } : {})

export type Rilettura = {
  stato: StatoThread; limite: number; casellaId: string | null; cartellaId: string | null
  /** «Solo non lette»: la rilettura usa lo stesso filtro dell'elenco. */
  nonLetti?: boolean
  /** Bandierina ed etichetta (538): gli stessi filtri dell'elenco. */
  bandierina?: string | null
  etichetta?: string | null
}

/** Prima pagina dell'elenco, per casella o della vista «Tutte le caselle» (casellaId nullo). */
export function rileggiElenco(r: Rilettura) {
  const q = new URLSearchParams({ stato: r.stato, limite: String(r.limite) })
  if (r.casellaId && r.cartellaId) q.set('cartella', r.cartellaId)
  if (r.nonLetti) q.set('non_letti', '1')
  if (r.bandierina) q.set('bandierina', r.bandierina)
  if (r.etichetta) q.set('etichetta', r.etichetta)
  const percorso = r.casellaId ? `/posta/caselle/${r.casellaId}/thread` : '/posta/tutte/thread'
  return api.get<PaginaThread>(`${percorso}?${q}`, { automatica: true })
}

export const rileggiCartelle = (casellaId: string) =>
  api.get<Cartella[]>(`/posta/caselle/${casellaId}/cartelle`, { automatica: true })

/** L'ordine dell'elenco: ultimo messaggio piu' recente prima, a parita' l'identificativo. */
function prima(a: VoceThread, b: VoceThread): boolean {
  return a.ultimo_il > b.ultimo_il || (a.ultimo_il === b.ultimo_il && a.id > b.id)
}

/**
 * La prima pagina appena riletta al posto di quella che si vede, senza perdere le pagine successive
 * gia' caricate scorrendo.
 *
 * La pagina nuova copre tutto cio' che e' piu' recente della sua ultima voce: quello che c'era prima
 * in quel tratto e non c'e' piu' e' stato archiviato, cestinato o spostato, e sparisce. Le voci piu'
 * vecchie restano come erano, e il cursore per proseguire resta quello di prima. Se si vedeva solo
 * la prima pagina, la nuova la sostituisce per intero, cursore compreso.
 */
export function unisciPagina(
  precedenti: VoceThread[], cursore: string | null, pagina: PaginaThread, limite: number,
): { voci: VoceThread[]; cursore: string | null } {
  if (precedenti.length <= limite || !pagina.prossimo_cursore) {
    return { voci: pagina.thread, cursore: pagina.prossimo_cursore }
  }
  const ultima = pagina.thread[pagina.thread.length - 1]
  if (!ultima) return { voci: pagina.thread, cursore: pagina.prossimo_cursore }
  const nuove = new Set(pagina.thread.map((v) => v.id))
  const coda = precedenti.filter((v) => !nuove.has(v.id) && prima(ultima, v))
  return { voci: [...pagina.thread, ...coda], cursore }
}
